import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Navigate } from "react-router-dom";
import { getSession } from "../../services/auth";
import { getTag } from "../../services/tag";
import { isAdmin } from "../../utilities/permissions";
import PageHeader from "../../components/layout/pageHeader";
import {
	canOpenInsightsDeckBuilder,
	INSIGHTS_DECK_BUILDER_ADMIN_TITLE,
	INSIGHTS_DECK_BUILDER_LOAD_ERROR,
	INSIGHTS_DECK_BUILDER_TITLE,
} from "./access";
import "./index.scss";

// Embeds the separately-developed/deployed Insights Deck Builder
// (insights-deck-builder-ui) as a same-origin iframe so its code never has to
// be pulled into this repo. In production the iframe is served behind the
// portal's edge (same origin) and the deck builder reads the portal's
// localStorage token; in local dev it points at the deck builder's dev server,
// which self-authenticates via its own .env.local.
//
// Default is the same-origin relative path: deployed environments serve the
// deck builder at /insights-deck-builder/ behind the portal, so a relative URL
// resolves to the current portal origin (https) — no mixed-content, env-agnostic.
// Local dev overrides this via public/runtime-env.js to point at the deck dev
// server (e.g. http://localhost:3030, run with PORT=3030 to avoid the CRA clash).
const DECK_BUILDER_URL =
	(window.__RUNTIME_CONFIG__ &&
		window.__RUNTIME_CONFIG__.REACT_APP_INSIGHTS_DECK_URL) ||
	"/insights-deck-builder/";

// ASB-4683: how far the deck builder scrolls before the title band folds away.
const BAND_COLLAPSE_AFTER_PX = 24;

const ACTIVE_SILO_POLL_MS = 250;
const ACTIVE_SILO_TIMEOUT_MS = 10000;

// inAudienceInsights: rendered as the Activation sub-page inside Audience
// Insights (the sub-menu and header come from there), where the setting
// decides for admins too. Otherwise it is the Admin Tools page.
function InsightsDeck({ inAudienceInsights = false }) {
	const roleId = getSession()?.role_id;
	const adminSkipsSetting = isAdmin(roleId) && !inAudienceInsights;
	const [tagId, setTagId] = useState(localStorage.getItem("activeSilo"));
	// "checking" until the active ArcTag's settings say whether the page may
	// open; then "open", "closed" or "error", and it stays decided. An ArcTag
	// switch in another tab after that leaves the open deck builder mounted
	// (ASB-4243), so only an undecided page follows activeSilo.
	// On the Admin Tools page an admin skips the settings check but, like
	// everyone, waits for an activeSilo: the deck builder reads it once, when
	// the iframe loads.
	const [access, setAccess] = useState(adminSkipsSetting && tagId != null ? "open" : "checking");

	useEffect(() => {
		if (access !== "checking") return undefined;
		const onStorage = () => setTagId(localStorage.getItem("activeSilo"));
		window.addEventListener("storage", onStorage);
		return () => window.removeEventListener("storage", onStorage);
	}, [access]);

	// A first visit has no activeSilo until the header picks the user's first
	// ArcTag: poll for it, and say the page could not load if none arrives.
	useEffect(() => {
		if (access !== "checking" || tagId != null) return undefined;
		const started = Date.now();
		const poll = setInterval(() => {
			const active = localStorage.getItem("activeSilo");
			if (active != null) setTagId(active);
			else if (Date.now() - started >= ACTIVE_SILO_TIMEOUT_MS) setAccess("error");
		}, ACTIVE_SILO_POLL_MS);
		return () => clearInterval(poll);
	}, [tagId, access]);

	useEffect(() => {
		if (access !== "checking" || tagId == null || !getSession()) return undefined;
		if (adminSkipsSetting) {
			setAccess("open");
			return undefined;
		}
		let cancelled = false;
		(async () => {
			try {
				const data = await getTag(tagId);
				if (!cancelled) {
					const open = canOpenInsightsDeckBuilder(roleId, data.data, { inAudienceInsights });
					setAccess(open ? "open" : "closed");
				}
			} catch (err) {
				if (!cancelled) setAccess("error");
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [tagId, access]);

	// ASB-4683: the title band folds away once the seller scrolls the deck
	// builder down, and comes back at the top, so the frame gets the band's
	// height while they work. The frame keeps doing the scrolling, so its own
	// tab and title rows stay pinned by the browser (a page that scrolled
	// instead could only re-pin them a frame late, and they bounced). AMS reads
	// the frame's scroll directly (same origin); a frame it can't read
	// (another origin, as in local dev) keeps the band.
	const frameRef = useRef(null);
	const bandRef = useRef(null);
	const [bandCollapsed, setBandCollapsed] = useState(false);

	useEffect(() => {
		const frame = frameRef.current;
		if (!frame) return undefined;
		let detach = null;
		const onLoad = () => {
			detach?.();
			detach = null;
			let win = null;
			let scroller = null;
			try {
				win = frame.contentWindow;
				scroller = win.document.scrollingElement;
			} catch (err) {
				scroller = null;
			}
			if (!scroller) {
				setBandCollapsed(false);
				return;
			}
			const onScroll = () => {
				const top = scroller.scrollTop;
				// How much the page has for the band and the frame, against the
				// frame's floor, both from the stylesheet (index.scss): the page
				// is at least the window minus the header, sub-menu and footer,
				// and the frame never goes below 480px. Read from the styles,
				// not the page's live height, which is briefly stale while the
				// window or the frame resizes.
				const pageEl = bandRef.current ? bandRef.current.parentElement : null;
				const room = pageEl ? parseFloat(window.getComputedStyle(pageEl).minHeight) || 0 : 0;
				const floor = parseFloat(window.getComputedStyle(frame).minHeight) || 0;
				setBandCollapsed((collapsed) => {
					// A folded band comes back at the top, or once the window is
					// too short for the page to fit even without it: the page
					// scrolls then, and the band scrolls away with it instead of
					// staying hidden.
					if (collapsed) return top > 0 && room >= floor;
					// Fold only when the frame still has more to scroll than the
					// band gives it: otherwise the taller frame scrolls back to
					// the top, the band returns, and the two flicker. And only
					// while the page fits the window with the band: on a short
					// one (a 1366x768 laptop) the frame is already at its floor
					// and the page scrolls, so folding would barely grow the
					// frame and make the page jump; the band scrolls away with
					// the page there anyway.
					const band = bandRef.current ? bandRef.current.offsetHeight : 0;
					const frameRoom = scroller.scrollHeight - scroller.clientHeight;
					return (
						top > BAND_COLLAPSE_AFTER_PX &&
						frameRoom > band + BAND_COLLAPSE_AFTER_PX &&
						room >= floor + band
					);
				});
			};
			win.addEventListener("scroll", onScroll, { passive: true });
			// Resizing the window changes whether the page fits. Checked on the
			// next frame: during the resize event itself the page can still
			// measure its old height for a moment.
			let resizeFrame = 0;
			const onResize = () => {
				cancelAnimationFrame(resizeFrame);
				resizeFrame = requestAnimationFrame(onScroll);
			};
			window.addEventListener("resize", onResize);
			onScroll();
			detach = () => {
				window.removeEventListener("resize", onResize);
				cancelAnimationFrame(resizeFrame);
				// The frame may have moved to another origin since (a download
				// link answered with an error page), where the browser refuses
				// to let AMS touch its window; its listener went with it.
				try {
					win.removeEventListener("scroll", onScroll);
				} catch (err) {
					// Nothing to remove.
				}
			};
		};
		frame.addEventListener("load", onLoad);
		// A frame that finished loading before this effect ran has already
		// fired its load event; check it now. (The initial about:blank
		// document doesn't count, and another origin reads as not loaded.)
		let loaded = false;
		try {
			loaded =
				frame.contentDocument?.readyState === "complete" &&
				frame.contentWindow.location.href !== "about:blank";
		} catch (err) {
			loaded = false;
		}
		if (loaded) onLoad();
		return () => {
			frame.removeEventListener("load", onLoad);
			detach?.();
		};
	}, [access]);

	// replace: Back then leaves the page instead of reopening it to be
	// redirected again.
	if (access === "closed")
		return (
			<Navigate
				to="/audience-insights/discovery"
				replace
			/>
		);
	if (access === "checking") return null;
	const loadError = (
		<p
			className="insights-deck-load-error"
			role="alert"
		>
			{INSIGHTS_DECK_BUILDER_LOAD_ERROR}
		</p>
	);

	// ASB-4556: one host layout for both entry points. The title shows only
	// once the page may open, and stays above a load error, as the Bulk
	// Reporting Tool's does. The band spans the page and the frame takes the
	// dashboards' width (.inner). The page is a flex column sized to the
	// viewport minus the sticky header, the sub-menu (Audience Insights only)
	// and the footer (index.scss), so the AMS footer stays pinned and only the
	// frame scrolls. Audience Insights keeps its sibling pages' title; Admin
	// Tools gets the product name alone.
	return (
		<div
			className={`insights-deck-page${inAudienceInsights ? " insights-deck-page--sub-nav" : ""}`}
		>
			<div
				ref={bandRef}
				className={`insights-deck-page__band${bandCollapsed ? " is-collapsed" : ""}`}
			>
				<div className="insights-deck-page__band-inner">
					<PageHeader
						title={
							inAudienceInsights ? INSIGHTS_DECK_BUILDER_TITLE : INSIGHTS_DECK_BUILDER_ADMIN_TITLE
						}
						titleClassName={inAudienceInsights ? "caps" : undefined}
					/>
				</div>
			</div>
			<div className="insights-deck-page__body">
				{access === "error" ? (
					loadError
				) : (
					<iframe
						ref={frameRef}
						title="Insights Deck Builder"
						className="inner insights-deck-frame"
						src={DECK_BUILDER_URL}
					/>
				)}
			</div>
		</div>
	);
}

InsightsDeck.propTypes = {
	inAudienceInsights: PropTypes.bool,
};

export default InsightsDeck;
